import { supabase } from '../config/supabase';

/**
 * Single source of truth for persisting agency settings.
 *
 * MULTI-TENANT ISOLATION:
 * Settings belong to a single agency. They are stored in Supabase per agency
 * (`user_registrations.data`, keyed by uid) AND cached in localStorage under an
 * AGENCY-SCOPED key (`appSettings_<agencyId>`), so two agencies sharing the same
 * browser/device can never see each other's settings.
 *
 * For backward compatibility, the CURRENTLY logged-in agency's settings are also
 * mirrored into the legacy `appSettings` key — many modules still read it — but
 * that mirror is always overwritten on login and cleared on logout, so it only
 * ever reflects the active agency.
 */

const LEGACY_KEY = 'appSettings';

// The localStorage cache key for the active agency.
function cacheKey(agencyId) {
  const a = agencyId || (typeof window !== 'undefined' ? window.__AGENCY_ID__ : null);
  return a ? `appSettings_${a}` : LEGACY_KEY;
}

// Write settings to BOTH the agency-scoped key and the legacy mirror.
export function writeSettingsCache(settings, agencyId) {
  try {
    const json = JSON.stringify(settings || {});
    localStorage.setItem(cacheKey(agencyId), json);
    localStorage.setItem(LEGACY_KEY, json); // mirror = current agency only
  } catch (e) {
    console.warn('writeSettingsCache failed:', e?.message);
  }
}

// Remove all cached settings (call on logout) so the next agency starts clean.
export function clearSettingsCache() {
  try {
    Object.keys(localStorage)
      .filter(k => k === LEGACY_KEY || k.startsWith('appSettings_'))
      .forEach(k => localStorage.removeItem(k));
  } catch (_) {}
}

/**
 * Persist settings for a specific agency.
 * @param {object} newSettings full settings object to persist
 * @param {string|null} userId  the authenticated user's uid (null = no DB sync)
 * @param {string|null} agencyId the active agency id (for the scoped cache key)
 */
export async function persistSettings(newSettings, userId, agencyId) {
  // 1) Local cache (agency-scoped + legacy mirror)
  writeSettingsCache(newSettings, agencyId);

  // 2) Supabase — scoped to THIS agency's row only.
  if (!userId) return { ok: true };
  try {
    const { data: reg } = await supabase
      .from('user_registrations')
      .select('data')
      .eq('uid', userId)
      .maybeSingle();

    const merged = { ...(reg?.data || {}), ...newSettings };

    const { error } = await supabase
      .from('user_registrations')
      .update({ data: merged })
      .eq('uid', userId);

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('persistSettings: Supabase sync failed:', error?.message || error);
    return { ok: false, error };
  }
}

/**
 * Load an agency's settings from Supabase. The result REPLACES (not merges with)
 * any previously cached settings beyond `base`, so keys belonging to another
 * agency can never leak into this one.
 */
export async function loadSettings(userId, base = {}, agencyId) {
  if (!userId) return base;
  try {
    const { data: reg } = await supabase
      .from('user_registrations')
      .select('data')
      .eq('uid', userId)
      .maybeSingle();
    if (reg?.data) {
      const merged = { ...base, ...reg.data }; // base = defaults only → no cross-agency leak
      writeSettingsCache(merged, agencyId);
      return merged;
    }
  } catch (e) {
    console.error('loadSettings failed:', e?.message || e);
  }
  return base;
}
